import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { trpc } from "@/trpc";
import { useAuth } from "@/state/AuthContext";
import { Button } from "@satyrsmc/shared/components";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";

function InvalidLink() {
  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <CardTitle>Invalid Link</CardTitle>
        <CardDescription>
          This signup link is invalid or has expired. Please contact an administrator.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Link to="/login">
          <Button variant="outline" className="w-full">
            Back to Login
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

/** Post-approval: user already has a user account, just needs to confirm birthday. */
function CompleteAccountForm({
  token,
  redirect,
  firstName,
}: {
  token: string;
  redirect: string;
  firstName?: string;
}) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [birthday, setBirthday] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const completeMutation = trpc.auth.completeMagicSignup.useMutation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!birthday) return;

    setSubmitting(true);
    try {
      const result = await completeMutation.mutateAsync({ token, birthday });
      setUser(result.user);
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <CardTitle>Complete Your Account</CardTitle>
        <CardDescription>
          {firstName ? `Welcome, ${firstName}! ` : ""}Please confirm your date of birth to activate
          your account.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="birthday" required>
                Date of Birth
              </Label>
              <span className="text-xs text-muted-foreground">Must be 21 or older</span>
            </div>
            <DatePicker id="birthday" className="w-full" value={birthday} onChange={setBirthday} />
          </div>
          <Button type="submit" className="w-full" disabled={submitting || !birthday}>
            {submitting ? "Activating..." : "Activate Account"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/** Self-registration: collect name, contact info, ICE, birthday, then wait for admin approval. */
function SelfRegistrationForm({ token, email }: { token: string; email: string }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [iceName, setIceName] = useState("");
  const [icePhone, setIcePhone] = useState("");
  const [birthday, setBirthday] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submitMutation = trpc.auth.submitSelfRegistration.useMutation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!birthday) return;

    setSubmitting(true);
    try {
      await submitMutation.mutateAsync({
        token,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        birthday,
        phone: phone.trim(),
        address_line1: addressLine1.trim(),
        address_line2: addressLine2.trim() || undefined,
        city: city.trim(),
        state: state.trim(),
        postal_code: postalCode.trim(),
        ice_name: iceName.trim(),
        ice_phone: icePhone.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Request Submitted</CardTitle>
          <CardDescription>
            Your registration request has been submitted. An admin will review it and you'll receive
            an email once your account is approved.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/login">
            <Button variant="outline" className="w-full">
              Back to Login
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Create Account</CardTitle>
        <CardDescription>
          Fill out the form below. An admin will review your request before your account is
          activated.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          {/* Personal info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Personal Info
            </h3>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} disabled />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first-name" required>
                  First Name
                </Label>
                <Input
                  id="first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name" required>
                  Last Name
                </Label>
                <Input
                  id="last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="birthday" required>
                  Date of Birth
                </Label>
                <span className="text-xs text-muted-foreground">Must be 21 or older</span>
              </div>
              <DatePicker
                id="birthday"
                className="w-full"
                value={birthday}
                onChange={setBirthday}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" required>
                Phone
              </Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                placeholder="(555) 555-5555"
                required
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Address
            </h3>
            <div className="space-y-2">
              <Label htmlFor="address-line1" required>
                Street Address
              </Label>
              <Input
                id="address-line1"
                type="text"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                autoComplete="address-line1"
                placeholder="123 Main St"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address-line2">Apt, Suite, etc.</Label>
              <Input
                id="address-line2"
                type="text"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                autoComplete="address-line2"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1 space-y-2">
                <Label htmlFor="city" required>
                  City
                </Label>
                <Input
                  id="city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  autoComplete="address-level2"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state" required>
                  State
                </Label>
                <Input
                  id="state"
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  autoComplete="address-level1"
                  placeholder="CA"
                  maxLength={2}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postal-code" required>
                  ZIP
                </Label>
                <Input
                  id="postal-code"
                  type="text"
                  inputMode="numeric"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  autoComplete="postal-code"
                  required
                />
              </div>
            </div>
          </div>

          {/* Emergency contact */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Emergency Contact
            </h3>
            <div className="space-y-2">
              <Label htmlFor="ice-name" required>
                Name
              </Label>
              <Input
                id="ice-name"
                type="text"
                value={iceName}
                onChange={(e) => setIceName(e.target.value)}
                autoComplete="off"
                placeholder="Full name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ice-phone" required>
                Phone
              </Label>
              <Input
                id="ice-phone"
                type="tel"
                value={icePhone}
                onChange={(e) => setIcePhone(e.target.value)}
                autoComplete="off"
                placeholder="(555) 555-5555"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={
              submitting ||
              !firstName.trim() ||
              !lastName.trim() ||
              !birthday ||
              !phone.trim() ||
              !addressLine1.trim() ||
              !city.trim() ||
              !state.trim() ||
              !postalCode.trim() ||
              !iceName.trim() ||
              !icePhone.trim()
            }
          >
            {submitting ? "Submitting..." : "Submit Registration"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export function SignupPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const redirect = searchParams.get("redirect") ?? "/";

  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  const validateQuery = trpc.auth.validateToken.useQuery(
    { token },
    { enabled: token.length > 0, retry: false },
  );

  useEffect(() => {
    if (validateQuery.isSuccess) {
      setTokenValid(validateQuery.data.valid);
    } else if (validateQuery.isError) {
      setTokenValid(false);
    }
  }, [validateQuery.isSuccess, validateQuery.isError, validateQuery.data]);

  if (token && tokenValid === null) {
    return (
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Validating Link</CardTitle>
          <CardDescription>Please wait while we validate your signup link.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" className="w-full" disabled>
            Validating...
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!token || tokenValid === false || !validateQuery.data) {
    return <InvalidLink />;
  }

  if (validateQuery.data.needs_registration) {
    return <SelfRegistrationForm token={token} email={validateQuery.data.email ?? ""} />;
  }

  return (
    <CompleteAccountForm
      token={token}
      redirect={redirect}
      firstName={validateQuery.data.first_name}
    />
  );
}
